/**
 * Service Worker anmelden und die App von selbst aktuell halten – fuer alle
 * Seiten gleich (Lead Sheets, Cloud, System).
 *
 *   1. Nach Updates suchen: beim Start, wenn die App wieder in den
 *      Vordergrund kommt, und stuendlich (reg.update()). Der neue Service
 *      Worker legt die neue Version ab und uebernimmt.
 *   2. Neue Version da: Ist die Seite gerade im Hintergrund oder frisch
 *      gestartet, wird still neu geladen. Sonst erscheint unten „Neue
 *      Version – Neu laden“ – mitten in der Probe oder beim Tippen laedt
 *      nichts ungefragt neu.
 *   3. Fehlt ein Teil der alten Version (Nachladefehler), wird einmal neu
 *      geladen statt einer leeren Seite; nie in einer Schleife.
 *
 * Daten der App (IndexedDB, Cloud) bleiben dabei unberuehrt.
 */

// Nach „Jetzt aktualisieren“ (?u=…): Adresse gleich wieder sauber machen.
if (new URLSearchParams(location.search).has('u')) {
  const clean = new URL(location.href);
  clean.searchParams.delete('u');
  history.replaceState(history.state, '', clean.href);
}

const RELOAD_KEY = 'sr:reload-at';
const LOOP_GUARD_MS = 30_000;
const START_GRACE_MS = 6_000;
const startedAt = Date.now();

/** Einmal neu laden – aber nicht, wenn wir das gerade eben schon getan haben. */
export function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < LOOP_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* ohne Speicher trotzdem einmal */
  }
  location.reload();
  return true;
}

/** Tippt jemand gerade oder ist die Buehne/der Teleprompter offen? */
function busy(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return true;
  return /#\/(stage|prompter)/.test(location.hash);
}

function offerReload(): void {
  if (document.querySelector('.sw-update')) return;
  const bar = document.createElement('div');
  bar.className = 'sw-update';
  bar.setAttribute('role', 'status');
  const text = document.createElement('span');
  text.textContent = 'Neue Version bereit';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn btn--primary btn--small';
  btn.textContent = 'Neu laden';
  btn.addEventListener('click', () => location.reload());
  bar.append(text, btn);
  document.body.append(bar);
  // Spaetestens wenn die App in den Hintergrund geht, still aktualisieren.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') location.reload();
  });
}

// Nachladefehler (vite:preloadError bzw. fehlendes Modul): einmal neu laden.
// Schon beim Laden dieser Datei aktiv – nicht erst, wenn der Start klappt.
window.addEventListener('vite:preloadError', (ev) => {
  if (reloadOnce()) ev.preventDefault();
});
window.addEventListener('unhandledrejection', (ev) => {
  const msg = String((ev.reason as { message?: unknown })?.message ?? ev.reason ?? '');
  if (/dynamically imported module|Importing a module script failed|error loading dynamically/i.test(msg)) reloadOnce();
});

const FORCED_KEY = 'sr:forced-update';

/**
 * Letzte Sicherung: Meldet der Server eine neuere Version, der Service
 * Worker bringt sie aber auch nach einer Weile nicht (z. B. weil eine Datei
 * beim Vorladen fehlschlug), wird einmal hart aktualisiert – je neuer
 * Version hoechstens einmal, damit es nie kreist.
 */
async function ensureLatest(reg: ServiceWorkerRegistration): Promise<void> {
  const { checkForUpdate, applyUpdate } = await import('../features/update-check.ts');
  const first = await checkForUpdate();
  if (first.state !== 'outdated') return;
  await reg.update().catch(() => undefined);
  await new Promise((r) => setTimeout(r, 20_000));
  const again = await checkForUpdate();
  if (again.state !== 'outdated') return;
  try {
    if (localStorage.getItem(FORCED_KEY) === again.remote) return;
    localStorage.setItem(FORCED_KEY, again.remote);
  } catch {
    return;
  }
  if (busy()) return offerReload();
  await applyUpdate();
}

export function registerServiceWorker(url: string, scope?: string): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const hadController = !!navigator.serviceWorker.controller;
  let handled = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Erste Installation: die Seite laeuft schon mit dem neuesten Stand.
    if (!hadController || handled) return;
    handled = true;
    if (document.visibilityState === 'hidden' || (Date.now() - startedAt < START_GRACE_MS && !busy())) location.reload();
    else offerReload();
  });

  navigator.serviceWorker
    .register(url, scope ? { scope } : undefined)
    .then((reg) => {
      const check = () => void reg.update().catch(() => undefined);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
      window.addEventListener('online', check);
      setInterval(check, 60 * 60 * 1000);
      setTimeout(() => void ensureLatest(reg).catch(() => undefined), 8_000);
    })
    .catch((err: unknown) => console.warn('Service Worker nicht registriert', err));
}
